import { useState } from 'react';
import type { AdacApplicationStatus } from '@/api/adac/adacApplication';
import { AdacActivationOfferModal } from './activationOffer/AdacActivationOfferModal';
import { AdacPolicyOverviewModal } from './policyOverview/AdacPolicyOverviewModal';
import type { BenefitModalProps } from '@/@types/benefitModalProps';
import { useAdacStatus } from '@/customHooks/useAdacStatus';
import { InsuranceActivationStatus } from '@/@types/insurance';
import { AdacUpsellModal } from './upsell/AdacUpsellModal';
import { AdacActivationPendingModal } from './activationPending/AdacActivationPendingModal';
import { AdacActivationApplicationModal } from './activationForm/AdacActivationApplicationModal';

export const Adac = ({ isVisible, onClose }: BenefitModalProps) => {
  const { isEntitled, status } = useAdacStatus();

  if (!isVisible) {
    return null;
  }
  if (!isEntitled) {
    return <AdacUpsellModal onClose={onClose} />;
  }
  return <AdacActivationFlow onClose={onClose} status={status} />;
};

const AdacActivationFlow = ({
  onClose,
  status,
}: Pick<BenefitModalProps, 'onClose'> & {
  status: ReturnType<typeof useAdacStatus>['status'];
}) => {
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [submission, setSubmission] = useState<{
    insuranceStatus: InsuranceActivationStatus;
    statusBeforeSubmission: typeof status;
  } | null>(null);
  // Show the submission result immediately until the backend status changes.
  const isActivationPending =
    status === InsuranceActivationStatus.PENDING ||
    (submission?.insuranceStatus === InsuranceActivationStatus.PENDING && submission.statusBeforeSubmission === status);

  const openApplication = () => setIsApplicationOpen(true);
  const recordSubmittedApplication = (application: AdacApplicationStatus) => {
    setSubmission({ insuranceStatus: application.insuranceStatus, statusBeforeSubmission: status });
    setIsApplicationOpen(false);
  };

  const close = () => {
    setIsApplicationOpen(false);
    setSubmission(null);
    onClose();
  };

  if (status === InsuranceActivationStatus.ACTIVATED) {
    return <AdacPolicyOverviewModal isVisible onClose={close} />;
  }
  if (isActivationPending) {
    return <AdacActivationPendingModal onClose={close} />;
  }
  if (isApplicationOpen) {
    return <AdacActivationApplicationModal onClose={close} onSubmitted={recordSubmittedApplication} />;
  }
  return <AdacActivationOfferModal onClose={close} onActivate={openApplication} />;
};
